import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, tap, catchError, throwError } from 'rxjs';
import * as CryptoJS from 'crypto-js';
import { CreateResponseBase } from '@models/base/createResponseBase.model';
import { CreatePermisoRequest } from '@models/seguridad/permisos/createPermisoRequest.model';
import { PermisoDto } from '@models/seguridad/permisos/permisoDto.model';
import { UpdatePermisoRequest } from '@models/seguridad/permisos/updatePermisoRequest.motel';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

@Injectable({
  providedIn: 'root'
})
export class PermisoService {
  // =================================
  // CONFIGURACIÓN
  // =================================
  private readonly CACHE_KEY = 'permisos_cache';
  private readonly ENCRYPTION_KEY = 'permisos_security_key';
  private readonly CACHE_DURATION = 30 * 60 * 1000; // 30 minutos

  // =================================
  // ESTADO INTERNO
  // =================================
  private permisosSubject = new BehaviorSubject<PermisoDto[] | null>(null);
  private cacheMap = new Map<string, CacheEntry<any>>();
  private currentRolId: number | null = null;

  constructor(private http: HttpClient) {
    this.initializeCache();
  }

  // =================================
  // INICIALIZACIÓN Y CONFIGURACIÓN
  // =================================

  private initializeCache(): void {
    try {
      this.loadCacheFromStorage();
      console.log('Cache de permisos inicializado');
    } catch (error) {
      console.error('Error al inicializar cache:', error);
      this.clearAllCache();
    }
  }

  /**
   * Inicializa los permisos para un rol específico
   * Llamar al inicio de la aplicación
   */
  initializePermissions(idRol: number): Promise<PermisoDto[]> {
    this.currentRolId = idRol;

    return this.getPermisosPorRol(idRol).toPromise()
      .then(permisos => {
        console.log(`Permisos inicializados para rol ${idRol}`);
        return permisos || [];
      })
      .catch(error => {
        console.error('Error al inicializar permisos:', error);
        return [];
      });
  }

  // =================================
  // OBSERVABLES PÚBLICOS
  // =================================

  /**
   * Observable público para suscribirse a cambios en permisos
   */
  get permisosObservable(): Observable<PermisoDto[] | null> {
    return this.permisosSubject.asObservable();
  }

  // =================================
  // MÉTODOS PRINCIPALES DE PERMISOS
  // =================================

  /**
   * Obtiene permisos por rol (principal)
   */
  getPermisosPorRol(idRol: number): Observable<PermisoDto[]> {
    // 1. Verificar BehaviorSubject
    const permisosActuales = this.permisosSubject.getValue();
    if (permisosActuales && this.currentRolId === idRol) {
      return of(permisosActuales);
    }

    // 2. Verificar cache persistente
    const cacheKey = `permisos_rol_${idRol}`;
    const cachedData = this.getCacheEntry<PermisoDto[]>(cacheKey);
    if (cachedData) {
      console.log('Permisos obtenidos del cache persistente');
      this.permisosSubject.next(cachedData);
      this.currentRolId = idRol;
      return of(cachedData);
    }

    // 3. Cargar desde servidor
    console.log('Cargando permisos desde servidor...');
    return this.getAllByIdRol(idRol).pipe(
      tap(permisos => {
        this.permisosSubject.next(permisos);
        this.currentRolId = idRol;
        console.log(`Permisos cargados para rol ${idRol}`);
      })
    );
  }

  /**
   * Obtiene permisos del cache (sincrono)
   */
  getPermisosCache(idRol?: number): PermisoDto[] | null {
    // Si tenemos datos en memoria y coincide el rol
    const permisosActuales = this.permisosSubject.getValue();
    if (permisosActuales && (!idRol || this.currentRolId === idRol)) {
      return permisosActuales;
    }

    // Si se especifica un rol, buscar en cache persistente
    if (idRol) {
      const cacheKey = `permisos_rol_${idRol}`;
      const cachedData = this.getCacheEntry<PermisoDto[]>(cacheKey);

      if (cachedData) {
        console.log('Permisos recuperados del cache para rol', idRol);
        this.permisosSubject.next(cachedData);
        this.currentRolId = idRol;
        return cachedData;
      }
    }

    return null;
  }

  /**
   * Verifica si un usuario tiene permisos específicos
   */
  hasPermissions(requiredTokens: string[] | string, modo: 'AND' | 'OR' = 'OR'): boolean {
  const permisos = this.permisosSubject.getValue();
  if (!permisos) return false;

  // Convertir a array si viene como string
  const tokensArray = Array.isArray(requiredTokens) ? requiredTokens : [requiredTokens];

  const userTokens = permisos.map(p => p.token.toUpperCase());
  const required = tokensArray.map(t => t.toUpperCase());

  return modo === 'AND'
    ? required.every(token => userTokens.includes(token))
    : required.some(token => userTokens.includes(token));
}

  // =================================
  // MÉTODOS DE API HTTP
  // =================================

  private getAllByIdRol(idRol: number): Observable<PermisoDto[]> {
    const cacheKey = `permisos_rol_${idRol}`;

    return this.http.get<PermisoDto[]>(`/api/Permiso/GetAllByIdRol?idRol=${idRol}`)
      .pipe(
        tap(data => this.setCacheEntry(cacheKey, data)),
        catchError(error => {
          console.error('Error al obtener permisos:', error);
          return throwError(() => error);
        })
      );
  }

  getAll(): Observable<PermisoDto[]> {
    return this.http.get<PermisoDto[]>("/api/Permiso/GetAll");
  }

  getById(id: number): Observable<PermisoDto> {
    return this.http.get<PermisoDto>(`/api/Permiso/GetById?id=${id}`);
  }

  create(request: CreatePermisoRequest): Observable<CreateResponseBase> {
    return this.http.post<CreateResponseBase>("/api/Permiso/Create", request)
      .pipe(tap(() => this.invalidateCache()));
  }

  update(request: UpdatePermisoRequest): Observable<any> {
    return this.http.put<any>("/api/Permiso/Update", request)
      .pipe(tap(() => this.invalidateCache()));
  }

  delete(id: number): Observable<any> {
    return this.http.delete<any>(`/api/Permiso/Delete?id=${id}`)
      .pipe(tap(() => this.invalidateCache()));
  }

  // =================================
  // GESTIÓN DE CACHE
  // =================================

  private setCacheEntry<T>(key: string, data: T): void {
    try {
      const entry: CacheEntry<T> = {
        data,
        timestamp: Date.now(),
        expiresAt: Date.now() + this.CACHE_DURATION
      };

      this.cacheMap.set(key, entry);
      this.saveCacheToStorage();
    } catch (error) {
      console.error('Error al guardar en cache:', error);
    }
  }

  private getCacheEntry<T>(key: string): T | null {
    try {
      const entry = this.cacheMap.get(key) as CacheEntry<T>;

      if (!entry || entry.expiresAt <= Date.now()) {
        this.cacheMap.delete(key);
        this.saveCacheToStorage();
        return null;
      }

      return entry.data;
    } catch (error) {
      console.error('Error al obtener del cache:', error);
      return null;
    }
  }

  private saveCacheToStorage(): void {
    try {
      const cacheData = Object.fromEntries(this.cacheMap);
      const encrypted = CryptoJS.AES.encrypt(JSON.stringify(cacheData), this.ENCRYPTION_KEY).toString();
      localStorage.setItem(this.CACHE_KEY, encrypted);
    } catch (error) {
      console.error('Error al guardar cache en storage:', error);
    }
  }

  private loadCacheFromStorage(): void {
    try {
      const encrypted = localStorage.getItem(this.CACHE_KEY);
      if (!encrypted) return;

      const decrypted = CryptoJS.AES.decrypt(encrypted, this.ENCRYPTION_KEY);
      const jsonString = decrypted.toString(CryptoJS.enc.Utf8);

      if (jsonString) {
        const cacheData = JSON.parse(jsonString);
        this.cacheMap = new Map(Object.entries(cacheData));
      }
    } catch (error) {
      console.error('Error al cargar cache:', error);
      this.clearAllCache();
    }
  }

  // =================================
  // LIMPIEZA Y UTILIDADES
  // =================================

  private invalidateCache(): void {
    if (this.currentRolId) {
      const cacheKey = `permisos_rol_${this.currentRolId}`;
      this.cacheMap.delete(cacheKey);
      this.saveCacheToStorage();
    }
    this.permisosSubject.next(null);
    this.currentRolId = null;
    console.log('Cache de permisos invalidado');
  }

  clearAllCache(): void {
    this.cacheMap.clear();
    localStorage.removeItem(this.CACHE_KEY);
    this.permisosSubject.next(null);
    this.currentRolId = null;
    console.log('Todo el cache limpiado');
  }

  /**
   * Llamar al cerrar sesión
   */
  clearOnLogout(): void {
    this.clearAllCache();
  }

  /**
   * Forzar recarga desde servidor
   */
  forceRefresh(idRol: number): Observable<PermisoDto[]> {
    this.invalidateCache();
    return this.getPermisosPorRol(idRol);
  }
}
